import SecureLS from "secure-ls"
import { createStore } from "vuex"
import { auth } from "./modules/auth.module"
import { data } from "./modules/data.module"
import { control } from "./modules/control.module"
import { realtime } from "./modules/realtime.module"
import { settings } from "./modules/settings.module"
import { integration } from "./modules/integration.module"
import { object_types_manager } from "./modules/object_types.module"
import { playbook } from "./modules/playbook.module"
import { ui } from "./modules/ui.module"
import { ALL_EMITTERS } from "./constants/all_emitters"

const PERSISTED_PATHS = ["auth"]

function createSecureModulePersistencePlugin(modules) {
  // Change this hash code in case you the data structure changed in server
  // to avoid  issues with logged in users
  const STORAGE_HASH = "hIXZSKRBuXdN"
  const ls = new SecureLS({ encodingType: "aes" })

  return (store) => {
    // Subscribe to store updates
    store.subscribe((mutation, state) => {
      // Check if the mutation belongs to any of the specified modules
      const is_targeted_mutation = modules.some((name) => mutation.type.startsWith(`${name}/`))

      if (is_targeted_mutation) {
        // Persist each specified module state to encrypted storage
        modules.forEach((module_name) => {
          const moduleState = state[module_name]
          ls.set(`${STORAGE_HASH}-${module_name}`, moduleState)
        })
      }
    })

    // Initialize module states from encrypted storage on store creation
    try {
      modules?.forEach((module_name) => {
        const persistedState = ls.get(`${STORAGE_HASH}-${module_name}`)
        if (persistedState) {
          // Replace the state of the specified module with the persisted state
          store.replaceState({
            ...store.state,
            [module_name]: persistedState
          })
        }
      })
    } catch (_) {
      // Clear the storage if any error occurs
      store.commit("auth/logout")
    }
  }
}

const store = createStore({
  getters: {
    vue() {
      return this._vm
    }
  },
  state() {
    return {
      object_types: [],
      metrics: [],
      organization: {},
      portfolio: {},
      ui_options: {}
    }
  },
  actions: {
    // Global action to handle voice transcription
    handleVoiceTranscription({ commit }, transcriptionData) {
      // Emit a global event that components can listen to
      if (this.$emitter) {
        this.$emitter.emit("VOICE_TRANSCRIPTION_RECEIVED", transcriptionData)
      }

      // Also commit to realtime module for logging
      commit("realtime/VOICE_TRANSCRIPTION", transcriptionData)
    }
  },
  mutations: {
    // access root state and reset it
    stateReset(state) {
      Object.assign(state, {
        object_types: [],
        object_types__type: [],
        metrics: [],
        organization: {},
        portfolio: {},
        ui_options: {}
      })
    },
    setObjectTypes(state, object_types) {
      state.object_types = object_types
      state.object_types__type = object_types.map((o) => o.type)
    },
    updateObjectType(state, object_type) {
      const index = state.object_types.findIndex((ot) => ot.id == object_type.id)
      state.object_types.splice(index, 1, object_type)
      state.object_types__type.splice(index, 1, object_type.type)
    },
    setMetrics(state, metrics) {
      state.metrics = metrics
    },
    setOrganization(state, organization) {
      state.organization = organization
    },
    setPortfolio(state, portfolio) {
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_ADVANCED_COMPANIES_METRICS_TABLE)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_OBJECTS_VIEW)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_OVERVIEW_COMPONENT)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_TABS)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_UPDATE_RECORD_FORM)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_ROLLUP_METRICS_GRID)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_NEW_RECORD_FORM)
      this.$emitter.emit(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_SIDE_MENU)
      state.portfolio = portfolio
    },
    setUIOptions(state, ui_options) {
      state.ui_options = ui_options
    },
    addMetric(state, metric) {
      state.metrics.unshift(metric)
    },
    updateMetric(state, metric) {
      const index = state.metrics.findIndex((m) => m.id == metric.id)
      state.metrics.splice(index, 1, metric)
    },
    deleteMetric(state, metric) {
      const index = state.metrics.findIndex((m) => m.id == metric.id)
      state.metrics.splice(index, 1)
    }
  },
  preserveState: true,
  modules: {
    auth: auth,
    data: data,
    control: control,
    realtime: realtime,
    settings: settings,
    integration: integration,
    objects: object_types_manager,
    playbook: playbook,
    ui: ui
  },
  plugins: [createSecureModulePersistencePlugin(PERSISTED_PATHS)]
})

export default store
